import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Chart, ChartPoint } from 'chart.js';
import moment from 'moment-timezone';
import { SystemService } from '../../../../../../system/system.service';
import { DataTypeComponent } from '../../../data-type-component';
import { ChartDO, FieldConfigDTO, ChartDatasetDO } from '../../../../../../system/system-dtos';
import { arrayNotEmpty } from '../../../../../utils/utils';

interface ChartDatasetContentDTO {
  label: string;
  points: ChartPoint[];
}

interface ChartDataDO {
  name: string;
  title: string;
  content: ChartDatasetContentDTO[];
}

export interface DatasetCache {
  id: number;
  key: string;
  data: any;
  datasetIndex: number;
}

Chart.defaults.global.elements.point.radius = 0;

@Component({
  selector: 'app-chart-form-field',
  templateUrl: './chart-form-field.component.html',
  styleUrls: ['./chart-form-field.component.scss'],
  standalone: false,
})
export class ChartFormFieldComponent extends DataTypeComponent implements OnInit, AfterViewInit {
  @Input() data: any;

  @Input() editMode: boolean;

  @Input() fieldConfig: FieldConfigDTO;

  @Input() formGroup: UntypedFormGroup;

  @Input() canDownload: boolean = true;

  public loading: boolean = true;

  public width: number = 300;

  public height: number = 300;

  datasetCache: { [id: number]: DatasetCache } = {};

  labelCache: string[] = [];

  charts: ChartDO[] = [];

  constructor(
    private _http: HttpClient,
    private systemService: SystemService,
    private formBuilder: UntypedFormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.data.charts) {
      // if ('list' === this.fieldConfig.propertyName) {
      const charts = this.data.charts as ChartDO[];
      charts.forEach((chart) => {
        this.charts.push(chart as ChartDO);
        if (chart.width > this.width) {
          this.width = chart.width;
        }
        if (chart.height > this.height) {
          this.height = this.data.height;
        }
      });
    } else {
      this.charts.push(this.data as ChartDO);
      this.width = this.data.width;
      this.height = this.data.height;
    }

    this.height += 50;
    this.width += 50;
  }

  ngAfterViewInit(): void {
    this.charts.forEach((chart: ChartDO) => {
      this.showChart(chart);
    });
    this.loading = false;
  }

  showChart(chartDef: ChartDO) {
    const canvas = <HTMLCanvasElement>document.getElementById(chartDef.key);
    const ctx = canvas.getContext('2d');

    if (chartDef.type === 'bar' || chartDef.type === 'horizontalBar' || chartDef.type === 'line') {
      let index = 0;

      if (arrayNotEmpty(chartDef.datasets)) {
        // const newDataSets: ChartDatasetDO[] = [];
        chartDef.datasets.forEach((dataset: ChartDatasetDO) => {
          index++;
          if (index >= dataset.backgroundColor.length) {
            index = 0;
          }

          dataset.backgroundColor = dataset.backgroundColor[index];
          dataset.borderColor = dataset.borderColor[index];
          dataset.borderWidth = dataset.borderWidth[0];
        });
      }
    }

    if (chartDef.type !== 'pie') {
      chartDef.data = {
        datasets: chartDef.datasets,
        labels: chartDef.labels,
      };
    }

    if (chartDef.type === 'pie') {
      chartDef.options.legend = {
        position: 'left',
      };
    }

    chartDef.chart = new Chart(ctx, chartDef);
  }

  downloadImage(chartKey: string) {
    const canvas = <HTMLCanvasElement>document.getElementById(chartKey);

    canvas.toBlob((d: any) => {
      const blob = new Blob([d as any], { type: 'image/png' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `chart_exported_on_${moment().format('YYYY_MM_DD_HH_mm')}`);

      document.body.appendChild(link);

      // link.setAttribute('download', filename);

      document.body.appendChild(link);
      link.click();
    });
  }

  getPieData(): any {
    return {
      type: 'pie',
      data: {
        labels: ['Jan', 'Feb'],
        datasets: [
          {
            data: [12, 8],
            backgroundColor: [
              'rgba(255, 99, 132, 0.2)',
              'rgba(54, 162, 235, 0.2)',
              'rgba(255, 206, 86, 0.2)',
              'rgba(75, 192, 192, 0.2)',
              'rgba(153, 102, 255, 0.2)',
              'rgba(255, 159, 64, 0.2)',
            ],
            borderColor: [
              'rgba(255, 99, 132, 1)',
              'rgba(54, 162, 235, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(153, 102, 255, 1)',
              'rgba(255, 159, 64, 1)',
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        cutoutPercentage: 50,
      },
    };
  }

  getLineData(): any {
    return {
      type: 'bar',
      data: {
        labels: ['Red', 'Blue', 'Yellow', 'Green', 'Purple', 'Orange'],
        datasets: [
          {
            label: '# of Votes',
            data: [12, 19, 3, 5, 2, 3],
            backgroundColor: [
              'rgba(255, 99, 132, 0.2)',
              'rgba(54, 162, 235, 0.2)',
              'rgba(255, 206, 86, 0.2)',
              'rgba(75, 192, 192, 0.2)',
              'rgba(153, 102, 255, 0.2)',
              'rgba(255, 159, 64, 0.2)',
            ],
            borderColor: [
              'rgba(255,99,132,1)',
              'rgba(54, 162, 235, 1)',
              'rgba(255, 206, 86, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(153, 102, 255, 1)',
              'rgba(255, 159, 64, 1)',
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
              },
            },
          ],
        },
      },
    };
  }
}
